import React, { useEffect, useState } from 'react'
import { Mail } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import Modal from '@/components/organisms/modals/Modal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils'
import type { OrgRole } from '@/types/organization'
import {
  ORG_LABEL,
  ORG_SELECT_CONTENT,
  ORG_SELECT_ITEM,
  ORG_SELECT_TRIGGER,
  ORG_SUBHEADING,
  OrgErrorBanner,
} from './OrgPrimitives'

interface InviteMemberModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: { email: string; role: OrgRole }) => Promise<void>
  /** OWNER only. The backend rejects an ADMIN inviting an OWNER with a 403. */
  canInviteOwner: boolean
  isSubmitting: boolean
  /** Server-side failure, e.g. "That email already has a pending invitation". */
  submitError: string | null
}

const ROLE_OPTIONS: { value: OrgRole; label: string; description: string }[] = [
  { value: 'MEMBER', label: 'Member', description: 'Can trade and see who else is in the org.' },
  { value: 'ADMIN', label: 'Admin', description: 'Can invite, remove and view member activity.' },
  { value: 'OWNER', label: 'Owner', description: 'Full control, including changing roles.' },
]

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Invite dialog for the Organization tab.
 *
 * Uses the codebase's hand-rolled `useState` + `validateForm(): Record<string, string>`
 * pattern rather than react-hook-form/zod. Those are installed but entirely unused
 * here, and a two-field form is not the place to introduce a second forms idiom.
 */
export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  canInviteOwner,
  isSubmitting,
  submitError,
}) => {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<OrgRole>('MEMBER')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset whenever the dialog is reopened so a previous attempt does not linger.
  useEffect(() => {
    if (isOpen) {
      setEmail('')
      setRole('MEMBER')
      setErrors({})
    }
  }, [isOpen])

  const options = canInviteOwner
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((option) => option.value !== 'OWNER')

  const validateForm = (): boolean => {
    const nextErrors: Record<string, string> = {}
    const trimmed = email.trim()

    if (!trimmed) {
      nextErrors.email = 'Email is required'
    } else if (!EMAIL_PATTERN.test(trimmed)) {
      nextErrors.email = 'Invalid email format'
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validateForm()) return
    await onSubmit({ email: email.trim(), role })
  }

  const selectedDescription = options.find((option) => option.value === role)?.description

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invite a member" contentClassName="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className={ORG_SUBHEADING}>
          We&apos;ll email an invitation link. It expires if it is not accepted.
        </p>

        {submitError && <OrgErrorBanner message={submitError} />}

        <div className="space-y-2">
          <label htmlFor="invite-email" className={ORG_LABEL}>
            Email address
          </label>
          <Input
            id="invite-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              if (errors.email) setErrors((prev) => ({ ...prev, email: '' }))
            }}
            placeholder="name@company.com"
            leftElement={<Mail size={16} className="text-muted-foreground" />}
            error={errors.email || undefined}
            className="font-plus-jakarta"
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <label className={ORG_LABEL}>Role</label>
          <Select
            value={role}
            onValueChange={(value) => setRole(value as OrgRole)}
            disabled={isSubmitting}
          >
            <SelectTrigger className={cn(ORG_SELECT_TRIGGER, 'h-12 w-full')}>
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent className={ORG_SELECT_CONTENT}>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value} className={ORG_SELECT_ITEM}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDescription && <p className={ORG_SUBHEADING}>{selectedDescription}</p>}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            Send invitation
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default InviteMemberModal
